import type {
  TData,
  TExamMarketGenericResponse,
} from "#/pages/exam-market/management";
import { api } from "#/utils";
import { useLoading } from "#/utils/context/loading";
import {
  Button,
  CheckBox,
  Modal,
  ProgressLinear,
  Toast,
  dayjs,
  useModal,
} from "@monta/design-system";
import { Close } from "@monta/icons";
import { fixString } from "@monta/router-react";
import clsx from "clsx";
import React, { useImperativeHandle, useMemo, useRef, useState } from "react";
import styles from "./ExamMarketModal.module.scss";

export type ExamMarketModalProps = object;

export type TExamMarketModalRef = {
  open: (row: TData, cb?: () => void) => void;
};

type TInfo = TExamMarketGenericResponse<{
  providerName: string;
  primaryProductName: string;
  examDatetime: Date;
  questionNo: number;
  examDetailInfo: string;
}>;

interface IExamDetailInfo {
  startQuestionNo: number;
  endQuestionNo: number;
  QuestionNO: number;
  duration: number;
  subModuleGroupId: number;
  subModuleGroupName: string;
  authors: string;
}

const ExamMarketModal = React.forwardRef<TExamMarketModalRef>(
  (props: ExamMarketModalProps, ref) => {
    const controller = useModal();
    const callbackRef = useRef<() => void>();
    const [consent, setConsent] = useState(false);
    const { showLoading, hideLoading, isLoading } = useLoading();

    const [showActions, setShowActions] = useState(false);
    const [info, setInfo] = useState<TInfo>();
    const [row, setRow] = useState<TData | null>(null);
    const examDetailInfo = useMemo<IExamDetailInfo[]>(
      () =>
        info?.examDetailInfo
          ? JSON.parse(info?.examDetailInfo).examDetailInfo
          : [],
      [info],
    );
    useImperativeHandle(ref, () => ({
      open(row, cb) {
        setRow(row);
        setConsent(false);
        setShowActions(!!cb);
        callbackRef.current = cb || controller.close;
        showLoading();
        controller.open();

        api
          .$post<TInfo>("/exam/getExamInfoToConfirm", null, {
            params: { primaryProductId: row.primaryProductId },
          })
          .then((res) => {
            setInfo(res);
          })
          .catch(() => {
            Toast.error({ message: "خطایی رخ داده", position: "top" });
            controller.close();
          })
          .finally(hideLoading);
      },
    }));
    return (
      <Modal {...controller} className={clsx(styles["modal"])}>
        <div className={styles["modal__header"]}>
          <p>
            {showActions
              ? "تایید نهایی برای اجرای آزمون"
              : "مشاهده جزئیات سوالات"}
          </p>
          <Close onClick={controller.close} color="#8C8787" />
        </div>
        {isLoading ? (
          <ProgressLinear />
        ) : (
          <>
            {showActions && (
              <div className={styles["modal__general-info"]}>
                <p>اطلاعات کلی آزمون</p>
                <div className={styles["modal__general-info__info"]}>
                  <span>
                    نام تامین کننده و انتشارات آموزشی:{" "}
                    <span>{info?.providerName}</span>
                  </span>
                  <span>
                    نام محصول: <span>{info?.primaryProductName}</span>
                  </span>
                  <span>
                    تاریخ آزمون:{" "}
                    <span>
                      {info?.examDatetime &&
                        dayjs(info.examDatetime)
                          .calendar("jalali")
                          .format("YYYY/MM/DD")}
                    </span>
                  </span>
                </div>
              </div>
            )}
            <div className={styles["modal__questions-detail"]}>
              <div className={styles["modal__questions-detail__header"]}>
                <p>ترتیب سوالات آزمون و گروه درسی</p>
                <p>
                  تعداد کل سوالات آزمون:{" "}
                  {examDetailInfo?.reduce((sum, i) => sum + i.QuestionNO, 0)}{" "}
                  سوال
                </p>
              </div>
              <ul className={styles["modal__questions-detail__list"]}>
                {examDetailInfo?.map((i, index) => (
                  <li key={index}>
                    <span>{index + 1}-</span>
                    <span>
                      از {i.startQuestionNo} تا {i.endQuestionNo} -{" "}
                      {i.subModuleGroupName} - مولفین: {i.authors}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            {showActions && (
              <>
                <CheckBox
                  color="#FF6A00"
                  className={styles["modal__consent"]}
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  label="تمامی موارد را چک کرده و از درستی همه موارد مطمئن هستم"
                />
                <div className={styles["modal__actions"]}>
                  <Button
                    onClick={() => {
                      window.open(
                        fixString(
                          `/#!examGenerator;a2=${row?.examRequestId};a4=${
                            row?.examRequestGrade
                          };a3=${row?.examRequestMajorId};a5=${encodeURIComponent(
                            row?.examRequestName || "",
                          )};a6=a8`,
                          "GWT_URL",
                        ),
                        "_target",
                      );
                    }}
                  >
                    ویرایش آزمون
                  </Button>
                  <Button
                    disabled={!consent}
                    onClick={() => {
                      controller.close();
                      callbackRef.current?.();
                    }}
                  >
                    تایید نهایی
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </Modal>
    );
  },
);

ExamMarketModal.displayName = "ExamMarketModal";
export default ExamMarketModal;
